import React, { useState, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useUsers } from '@/hooks/useUsers'
import { useModulesByCourse } from '@/hooks/useModules'
import {
  useInstructorSplits,
  useAssignInstructor,
  useRemoveInstructor,
} from '@/hooks/useInstructorSplits'
import { instructorSplitService } from '@/services/instructorSplitService'
import type { InstructorSplit, AssignInstructorInput, UpdateSplitInput } from '@/types/instructorSplit'
import { InstructorSplitCard } from './InstructorSplitCard'
import { InstructorSplitForm } from './InstructorSplitForm'

interface InstructorAssignmentPanelProps {
  courseId: string
}

export const InstructorAssignmentPanel: React.FC<InstructorAssignmentPanelProps> = ({ courseId }) => {
  const [showForm, setShowForm] = useState(false)
  const [editingSplit, setEditingSplit] = useState<InstructorSplit | null>(null)
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)

  const queryClient = useQueryClient()

  // Fetch data
  const { data: splits, isLoading: isLoadingSplits } = useInstructorSplits(courseId)
  const { data: allInstructors, isLoading: isLoadingInstructors } = useUsers({ role: 'instructor' })
  const { data: modules, isLoading: isLoadingModules } = useModulesByCourse(courseId)

  // Mutations
  const assignMutation = useAssignInstructor(courseId)
  const removeMutation = useRemoveInstructor(courseId)

  // Calculate total percentage
  const totalPercentage = useMemo(() => {
    return (splits || []).reduce((sum, split) => sum + split.percentage, 0)
  }, [splits])

  // Get instructor name by ID
  const getInstructorInfo = (instructorId: string) => {
    const instructor = allInstructors?.find((i) => i.id === instructorId)
    return {
      name: instructor?.name ?? 'Unknown',
      email: instructor?.email ?? '',
    }
  }

  // Filter available instructors (not yet assigned)
  const availableInstructors = useMemo(() => {
    const assignedIds = new Set((splits || []).map((s) => s.instructorId))
    return (allInstructors || []).filter((i) => !assignedIds.has(i.id))
  }, [allInstructors, splits])

  const handleAssign = async (data: AssignInstructorInput[] | UpdateSplitInput) => {
    if (editingSplit) {
      // Edit mode - handled by individual update mutation
      return
    }

    // Add mode - assign multiple instructors
    const assignments = data as AssignInstructorInput[]
    try {
      for (const assignment of assignments) {
        await assignMutation.mutateAsync(assignment)
      }
      setShowForm(false)
    } catch (error) {
      console.error('Failed to assign instructors:', error)
    }
  }

  const handleEdit = (split: InstructorSplit) => {
    setEditingSplit(split)
    setShowForm(true)
  }

  const handleUpdate = async (data: AssignInstructorInput[] | UpdateSplitInput) => {
    if (!editingSplit) return

    const updateData = data as UpdateSplitInput
    setIsUpdating(true)

    try {
      await instructorSplitService.updateSplit(courseId, editingSplit.instructorId, updateData)
      // Invalidate queries to refetch
      queryClient.invalidateQueries({ queryKey: ['instructor-splits', courseId] })
      setEditingSplit(null)
      setShowForm(false)
    } catch (error) {
      console.error('Failed to update split:', error)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleRemove = async (userId: string) => {
    if (!confirm('Remove this instructor from the course?')) return

    setRemovingId(userId)
    try {
      await removeMutation.mutateAsync(userId)
    } catch (error) {
      console.error('Failed to remove instructor:', error)
    } finally {
      setRemovingId(null)
    }
  }

  const handleCancel = () => {
    setShowForm(false)
    setEditingSplit(null)
  }

  // Loading state
  if (isLoadingSplits || isLoadingInstructors || isLoadingModules) {
    return (
      <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Course Instructors</h3>
          <p className="text-sm text-gray-500 dark:text-text-muted mt-1">
            Assign instructors and configure commission splits
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            disabled={availableInstructors.length === 0}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg font-bold text-sm hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="material-symbols-outlined text-lg">person_add</span>
            Add Instructor
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <div className="mb-6 p-4 rounded-lg bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10">
          <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-4">
            {editingSplit ? 'Edit Split' : 'Assign New Instructors'}
          </h4>
          <InstructorSplitForm
            availableInstructors={availableInstructors}
            availableModules={modules ?? []}
            existingSplit={editingSplit ?? undefined}
            existingInstructorName={editingSplit ? getInstructorInfo(editingSplit.instructorId).name : undefined}
            currentTotalPercentage={totalPercentage}
            onSubmit={editingSplit ? handleUpdate : handleAssign}
            onCancel={handleCancel}
            isLoading={assignMutation.isPending || isUpdating}
          />
        </div>
      )}

      {/* Instructor List */}
      {(splits?.length ?? 0) > 0 ? (
        <div className="space-y-3">
          {splits?.map((split) => {
            const info = getInstructorInfo(split.instructorId)
            return (
              <InstructorSplitCard
                key={split.id}
                split={split}
                instructorName={info.name}
                instructorEmail={info.email}
                onEdit={handleEdit}
                onRemove={handleRemove}
                isRemoving={removingId === split.instructorId}
              />
            )
          })}
        </div>
      ) : !showForm ? (
        <div className="text-center py-12 text-gray-500 dark:text-text-muted">
          <span className="material-symbols-outlined text-5xl mb-3 opacity-50">group_off</span>
          <p className="text-lg mb-2">No instructors assigned</p>
          <p className="text-sm mb-4">Add instructors to configure commission splits</p>
          <button
            onClick={() => setShowForm(true)}
            disabled={availableInstructors.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg font-bold text-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            <span className="material-symbols-outlined text-lg">person_add</span>
            Add First Instructor
          </button>
          {availableInstructors.length === 0 && (
            <p className="text-xs text-red-400 mt-2">No instructors available to assign</p>
          )}
        </div>
      ) : null}
    </div>
  )
}

export default InstructorAssignmentPanel
